import { ClaimDetailsModal } from "components/ClaimDetailsModal/ClaimDetailsModal";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useGetClaimByIdQuery } from "store/queries/claims";
import { useGetRemitAccuracyClusterQuery } from "store/queries/remitAccuracy";
import {
  IRemitCluster,
  IRemitClusterLine,
  IRemitClusterRequest,
  RemitScoredBenefit,
} from "types/RemitAccuracy/RemitAccuracy";

import {
  formatRemitValue,
  lineLabel,
  READ_FROM_LABELS,
  REMIT_BENEFIT_LABELS,
  REMIT_KIND_LABELS,
  REMIT_OUTCOME_LABELS,
} from "./remitAccuracyWording";

type IClusterRow = IRemitCluster["rows"][number];

const PAGE_SIZE = 100;

/** Only the first checks' stored responses are read; the rest say so rather than showing a blank. */
const remitLineBody = (row: IClusterRow) => {
  if (!row.examined)
    return <span className="text-color-secondary">not examined</span>;
  return row.remitLine ?? "—";
};

function ClaimModal({
  claimId,
  accountId,
  onHide,
}: {
  claimId: string;
  accountId: string;
  onHide: () => void;
}) {
  const { data } = useGetClaimByIdQuery({ claimId });
  if (!data) return null;
  return (
    <ClaimDetailsModal
      visible
      onHide={onHide}
      claim={data}
      accountId={accountId}
    />
  );
}

const outcomeBody = (row: IClusterRow) => (
  <Tag
    severity={row.outcome === "MATCH" ? "success" : "danger"}
    value={REMIT_OUTCOME_LABELS[row.outcome]}
  />
);

const idBody = (row: IClusterRow) => (
  <span className="text-xs white-space-nowrap">{row.pverifyResponseId}</span>
);

/**
 * CLE-2818 — one miss cluster: the checks in it, and what an example stored response looks like on the
 * service line the engine read. Read-only.
 */
export function RemitAccuracyCluster({
  request,
  onBack,
}: {
  request: IRemitClusterRequest;
  onBack: () => void;
}) {
  const [offset, setOffset] = useState(0);
  const { data, isLoading, isFetching, isError } =
    useGetRemitAccuracyClusterQuery({ ...request, limit: PAGE_SIZE, offset });
  const [openClaim, setOpenClaim] = useState<string | null>(null);
  const cluster = data?.data;
  const benefit = request.benefit as RemitScoredBenefit;
  const valueHeader =
    benefit === "COINSURANCE_PERCENT" ? "Co-insurance" : "Copay";

  const value = (amount: number | null) => formatRemitValue(amount, benefit);

  const lineTagBody = (line: IRemitClusterLine) => (
    <div className="flex gap-2">
      {line.engineRead && (
        <Tag
          severity="danger"
          value={
            line.engineRead === "SUMMARY" ? "Engine (via summary)" : "Engine"
          }
        />
      )}
      {line.equalsRemit && <Tag severity="success" value="= Remit" />}
    </div>
  );

  const linksBody = (row: IClusterRow) => (
    <div className="flex gap-2 align-items-center white-space-nowrap">
      {row.orderIds[0] && (
        <Link to={`/orders/${row.orderIds[0]}`} target="_blank">
          Estimate
        </Link>
      )}
      {row.claimIds[0] && (
        <Button
          type="button"
          label="Claim"
          className="p-button-link p-button-sm p-0"
          onClick={() => setOpenClaim(row.claimIds[0])}
        />
      )}
    </div>
  );

  if (isError) {
    return <Message severity="error" text="The cluster could not be loaded." />;
  }

  return (
    <div data-testid="remitAccuracy.cluster">
      <Button
        type="button"
        icon="pi pi-angle-left"
        label="Remit accuracy"
        className="p-button-text p-button-sm px-0"
        onClick={onBack}
      />
      <h3 className="mt-2 mb-2">
        {cluster
          ? `${cluster.cluster.accountName} · ${cluster.cluster.payerName} · ${
              cluster.cluster.planName ?? "No plan name"
            } · ${REMIT_BENEFIT_LABELS[benefit]}`
          : "Loading…"}
      </h3>
      {cluster && (
        <div className="flex flex-wrap gap-2 align-items-center mb-3">
          <Tag severity="danger" value={`${cluster.cluster.misses} misses`} />
          <Tag
            severity="success"
            value={`${cluster.cluster.matches} matches`}
          />
          <span className="text-color-secondary">
            {cluster.cluster.checks} remit-confirmed checks · engine read from{" "}
            {READ_FROM_LABELS[cluster.cluster.readFrom] ??
              cluster.cluster.readFrom}{" "}
            · {REMIT_KIND_LABELS[cluster.cluster.kind]}
          </span>
        </div>
      )}

      <div className="grid mb-3">
        <div className="col-12 lg:col-7">
          <div className="border-1 surface-border border-round p-3 h-full">
            <h4 className="mt-0 mb-2">What the response looks like</h4>
            {cluster?.remitLinePattern && (
              <p className="mt-0">
                The remit equals the{" "}
                <strong>{cluster.remitLinePattern.line}</strong> line on{" "}
                {cluster.remitLinePattern.checks} of{" "}
                {cluster.remitLinePattern.examined} checks examined.
              </p>
            )}
            {cluster?.example ? (
              <>
                <p className="text-xs text-color-secondary mt-0">
                  Example response {cluster.example.pverifyResponseId} ·
                  RequestID {cluster.example.pverifyRequestId ?? "—"} · lines as
                  pVerify sent them, before this account&apos;s payer filters
                </p>
                <DataTable
                  value={cluster.example.lines}
                  emptyMessage="No in-network lines on the service line the engine read."
                  rowClassName={(line: IRemitClusterLine) => ({
                    "bg-green-50": line.equalsRemit,
                  })}
                  data-testid="remitAccuracy.cluster.lines"
                >
                  <Column
                    header="Line message"
                    body={(line: IRemitClusterLine) => lineLabel(line)}
                  />
                  <Column header="Network" body={() => "In"} />
                  <Column
                    header={valueHeader}
                    body={(line: IRemitClusterLine) => value(line.value)}
                  />
                  <Column header="" body={lineTagBody} />
                </DataTable>
                {cluster.example.outOfNetworkLines > 0 && (
                  <p className="text-color-secondary mb-0">
                    {cluster.example.outOfNetworkLines} out-of-network lines not
                    shown.
                  </p>
                )}
              </>
            ) : (
              !isLoading && <p className="m-0">No example response.</p>
            )}
          </div>
        </div>
        <div className="col-12 lg:col-5">
          {/* Static placeholder — rule recommendations are a later ticket. */}
          <div
            className="border-1 border-dashed surface-border border-round p-3 h-full flex flex-column gap-2 justify-content-center"
            data-testid="remitAccuracy.cluster.recommendationsPlaceholder"
          >
            <Tag
              severity="info"
              value="Coming soon"
              className="align-self-start"
            />
            <h4 className="m-0">Rule recommendations</h4>
            <p className="m-0 text-color-secondary">
              A suggested configuration change for this cluster will appear
              here, with the checks it would fix and break before anyone applies
              it.
            </p>
          </div>
        </div>
      </div>

      <h4 className="mb-2">Checks in this cluster · misses first</h4>
      <DataTable
        value={cluster?.rows ?? []}
        loading={isFetching}
        dataKey="pverifyResponseId"
        lazy
        paginator
        rows={PAGE_SIZE}
        first={offset}
        totalRecords={cluster?.total ?? 0}
        onPage={(event) => setOffset(event.first)}
        emptyMessage="No checks."
        data-testid="remitAccuracy.cluster.checks"
      >
        <Column header="Outcome" body={outcomeBody} />
        <Column header="Plan" field="planName" />
        <Column header="pVerify response" body={idBody} />
        <Column header="RequestID" field="pverifyRequestId" />
        <Column
          header="Engine"
          body={(row: IClusterRow) => value(row.engineValue)}
        />
        <Column
          header="Remit"
          body={(row: IClusterRow) =>
            row.remitValue === null
              ? row.remitValues.map(value).join(", ")
              : value(row.remitValue)
          }
        />
        <Column header="Line equal to the remit" body={remitLineBody} />
        <Column header="" body={linksBody} />
      </DataTable>

      {openClaim && (
        <ClaimModal
          claimId={openClaim}
          accountId={request.accountId}
          onHide={() => setOpenClaim(null)}
        />
      )}
    </div>
  );
}
